<template>
  <div :class="['progress-circle', severityClass, sizeClass]">
    <svg
      viewBox="0 0 36 36"
      class="progress-circle__svg"
    >
      <!-- Background path -->
      <path
        class="progress-circle__bg"
        d="
          M18 2
          a 16 16 0 0 1 0 32
          a 16 16 0 0 1 0 -32
        "
      />
      <!-- Animated progress path -->
      <path
        class="progress-circle__progress"
        d="
          M18 2
          a 16 16 0 0 1 0 32
          a 16 16 0 0 1 0 -32
        "
        :style="progressStyle"
      />
    </svg>
    <!-- Value in the center -->
    <div
      class="progress-circle__value"
      :class="['text-' + size]"
    >
      <div v-if="showValue && !$slots['display-value']">{{ centerValue }}</div>
      <slot
        name="display-value"
        class="progress-circle__value"
        :class="['text-' + size]"
        :value="centerValue"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Number, default: 0 }, // The percentage (0 to 100 typically)
  severity: { type: String, default: '' }, // e.g. 'brand', 'primary', etc.
  size: { type: String, default: 'md' }, // e.g. 'xs', 'sm', 'md', 'lg', 'xl'
  showValue: { type: Boolean, default: true },
  displayedValue: { type: Number, default: null } // Optional displayed value (doesn't affect progress bar)
})

const emit = defineEmits(['update:modelValue', 'update:displayedValue'])

// Internal state to animate between old and new values
const currentValue = ref(props.modelValue)

// Whenever the prop changes, animate from old to new
watch(
  () => props.modelValue,
  newVal => {
    animateValue(currentValue.value, newVal)
  }
)

// Optional: allow two-way binding if needed
watch(currentValue, val => {
  emit('update:modelValue', val)
})

// The displayed value in the center
// If displayedValue prop is provided (not null), use that; otherwise use the actual progress value
const centerValue = computed(() => {
  if (props.displayedValue !== null && props.displayedValue !== undefined) {
    return props.displayedValue
  }
  return Math.round(currentValue.value)
})

function animateValue(from, to) {
  const frames = 15
  const step = (to - from) / frames
  let count = 0

  const interval = setInterval(() => {
    currentValue.value = from + step * count
    count++

    if (count >= frames) {
      clearInterval(interval)
      currentValue.value = to // Ensure exact final value
    }
  }, 20)
}

// Progress path offset (circle circumference ~ 100.53 for r=16)
const circumference = 100.53
const progressStyle = computed(() => {
  const dashOffset = ((100 - currentValue.value) / 100) * circumference
  return {
    strokeDashoffset: dashOffset
  }
})

const severityClass = computed(() => {
  return props.severity ? `progress-circle--${props.severity}` : ''
})

const sizeClass = computed(() => {
  return props.size ? `progress-circle--${props.size}` : ''
})
</script>

<style scoped>
.progress-circle {
  position: relative;
  /* display: inline-block; */
  width: 80px; /* default */
  height: 80px;
}

.progress-circle__svg {
  width: 100%;
  height: 100%;
  /* transform: rotate(-90deg); */
}

.progress-circle__bg,
.progress-circle__progress {
  fill: none;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-dasharray: 100.53; /* approximate circle circumference */
}

.progress-circle__bg {
  stroke: var(--bg-tertiary);
}

.progress-circle__progress {
  stroke: var(--fg-brand); /* default color if no severity class is used */
  transition: stroke-dashoffset 0.3s ease-out;
}

.progress-circle__value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Sizes */
.progress-circle--xs {
  width: 30px;
  height: 30px;
  font-weight: 500;
}
.progress-circle--sm {
  width: 40px;
  height: 40px;
  font-weight: 600;
}
.progress-circle--md {
  width: 55px;
  height: 55px;
  font-weight: bold;
}
.progress-circle--lg {
  width: 65px;
  height: 65px;
  font-weight: bold;
}
.progress-circle--xl {
  width: 80px;
  height: 80px;
  font-weight: bold;
}

/* Severity classes (override stroke color) */
.progress-circle--brand .progress-circle__progress {
  stroke: rebeccapurple;
}
.progress-circle--primary .progress-circle__progress {
  stroke: #007bff;
}
.progress-circle--secondary .progress-circle__progress {
  stroke: #6c757d;
}
.progress-circle--warning .progress-circle__progress {
  stroke: #ffc107;
}
.progress-circle--info .progress-circle__progress {
  stroke: #17a2b8;
}
.progress-circle--error .progress-circle__progress {
  stroke: #dc3545;
}
.progress-circle--success .progress-circle__progress {
  stroke: #28a745;
}
</style>
